import React, { useState, useEffect, useCallback, memo } from 'react'
import { Grid, Button, Tabs, Tab, Typography } from '@mui/material'

// styles
import { useTranslation } from 'react-i18next'
import useStyles from './styles'

// components
import PageTitle from '../../components/PageTitle'
import { useDataApi } from '../../context/DataApiContext'
import DocViewer, { DocViewerRenderers } from 'react-doc-viewer'
import Loading from '../../components/Loading'
import { useNotificationsProvider } from '../../context/NotificationsContext'
import { useConfirmationDialogProvider } from '../../context/ConfirmationDialogContext'
import ManagePVAdmissionDialog from '../../components/PVAdmissionManagement'
import CreateTemplateDialog from '../../components/PVAdmissionTemplateDialogs/CreateTemplateDialog'
import RenameTemplateDialog from '../../components/PVAdmissionTemplateDialogs/RenameTemplateDialog'
import { getPVAdmissionErrorMessages } from '../../utils/pvAdmissionTemplateErrors'

const KEY = 'pv-admission-management'

const DOC_VIEWER_CONFIG = {
  header: {
    disableHeader: false,
    disableFileName: false,
    retainURLParams: false,
  },
}

// DocViewer reloads the document whenever it gets a new documents array, so
// the preview only renders again when the document URL changes (not when a
// dialog of the page opens or closes).
const TemplatePreview = memo(function TemplatePreview({ documentUrl }) {
  return (
    <DocViewer
      key={documentUrl}
      style={{ height: 550 }}
      config={DOC_VIEWER_CONFIG}
      pluginRenderers={DocViewerRenderers}
      documents={[{ uri: documentUrl, fileType: 'doc' }]}
    />
  )
})

export default function PVAdmissionManagement() {
  const { t } = useTranslation()
  const classes = useStyles()
  const { dataProvider } = useDataApi()
  const { showNotification } = useNotificationsProvider()
  const { showConfirmationDialog } = useConfirmationDialogProvider()

  const [templates, setTemplates] = useState(null)
  const [templateId, setTemplateId] = useState(null)
  const [openManagePVAdmission, setOpenManagePVAdmission] = useState(false)
  const [openCreate, setOpenCreate] = useState(false)
  const [templateToRename, setTemplateToRename] = useState(null)

  const fetchTemplates = useCallback(
    (selectId) =>
      dataProvider.pvAdmissionTemplates
        .getAll()
        .then((items) => {
          setTemplates(items)
          setTemplateId((current) => {
            const wanted = selectId || current
            if (items.some((item) => item.id === wanted)) {
              return wanted
            }
            return items.length > 0 ? items[0].id : null
          })
        })
        .catch(() => {
          setTemplates([])
          showNotification('error', t('errors.api-error'))
        }),
    [dataProvider, showNotification, t]
  )

  useEffect(() => {
    fetchTemplates()
  }, [fetchTemplates])

  const selected = (templates || []).find((item) => item.id === templateId)

  const onDelete = () => {
    const { id, name } = selected
    showConfirmationDialog(
      t(`${KEY}.confirm-delete`, { name }),
      () => {
        dataProvider.pvAdmissionTemplates
          .remove(id)
          .then(() => {
            showNotification('success', t(`${KEY}.delete-success`, { name }))
            return fetchTemplates()
          })
          .catch((err) =>
            getPVAdmissionErrorMessages(err, t).forEach((message) =>
              showNotification('error', message)
            )
          )
      },
      t(`${KEY}.delete`)
    )
  }

  if (templates === null) {
    return <Loading />
  }

  return (
    <>
      <ManagePVAdmissionDialog
        open={openManagePVAdmission}
        templateId={templateId}
        onClose={() => setOpenManagePVAdmission(false)}
        onCreated={() => {
          showNotification('success', t(`${KEY}.upload-success`))
          fetchTemplates()
          setOpenManagePVAdmission(false)
        }}
      />
      <CreateTemplateDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onCreated={(template) => {
          setOpenCreate(false)
          fetchTemplates(template.id)
        }}
      />
      <RenameTemplateDialog
        template={templateToRename}
        onClose={() => setTemplateToRename(null)}
        onRenamed={() => {
          setTemplateToRename(null)
          fetchTemplates()
        }}
      />
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item xs={12} sm={4}>
          <PageTitle title={t(`${KEY}.title`)} />
        </Grid>
        <Grid item container sm={8} justifyContent="flex-end" spacing={1}>
          <Grid item>
            <Button
              variant="outlined"
              color="primary"
              className={classes.button}
              onClick={() => setOpenCreate(true)}
            >
              + {t(`${KEY}.add-template`)}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              disabled={!selected}
              onClick={() => setOpenManagePVAdmission(true)}
            >
              {t(`${KEY}.manage-pv-admission`)}
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={12}>
          <Tabs
            value={selected ? selected.id : false}
            onChange={(event, newValue) => setTemplateId(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
          >
            {templates.map((template) => (
              <Tab
                key={template.id}
                value={template.id}
                label={
                  template.hasDocument
                    ? template.name
                    : `${template.name} (${t(`${KEY}.no-document`)})`
                }
              />
            ))}
          </Tabs>
        </Grid>
      </Grid>
      {selected && (
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Typography variant="body2" data-testid="vehicle-types-count">
              {t(`${KEY}.vehicle-types-count`, {
                count: selected.vehicleTypesCount,
              })}
            </Typography>
          </Grid>
          <Grid item>
            <Button size="small" onClick={() => setTemplateToRename(selected)}>
              {t(`${KEY}.rename`)}
            </Button>
          </Grid>
          {!selected.legacyType && (
            <Grid item>
              <Button size="small" color="error" onClick={onDelete}>
                {t(`${KEY}.delete`)}
              </Button>
            </Grid>
          )}
        </Grid>
      )}
      {selected && selected.hasDocument && (
        <Grid container spacing={4} className={classes.previewGridContainer}>
          <Grid item className={classes.previewGridItem}>
            <TemplatePreview documentUrl={selected.documentUrl} />
          </Grid>
        </Grid>
      )}
    </>
  )
}
